import type { OHLCV, Timeframe } from '../types/ohlcv';
import { timeframeToMs } from './timeframe';

const DAY_MS = 86_400_000;
const WEEK_MS = 7 * DAY_MS;
// 1970-01-01 was a Thursday; the Monday before it is -3 days.
const MONDAY_EPOCH_OFFSET = 3 * DAY_MS;

function weekStart(timestamp: number): number {
  return Math.floor((timestamp + MONDAY_EPOCH_OFFSET) / WEEK_MS) * WEEK_MS - MONDAY_EPOCH_OFFSET;
}

function monthStart(timestamp: number): number {
  const d = new Date(timestamp);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
}

function rollUp(candles: OHLCV[], bucketOf: (timestamp: number) => number): OHLCV[] {
  const buckets = new Map<number, OHLCV>();
  for (const candle of [...candles].sort((a, b) => a.timestamp - b.timestamp)) {
    const start = bucketOf(candle.timestamp);
    const existing = buckets.get(start);
    if (!existing) {
      buckets.set(start, { ...candle, timestamp: start });
    } else {
      existing.high = Math.max(existing.high, candle.high);
      existing.low = Math.min(existing.low, candle.low);
      existing.close = candle.close;
      existing.volume += candle.volume;
    }
  }
  return [...buckets.values()];
}

/** Daily → weekly candles, ISO weeks starting Monday 00:00 UTC. */
export function aggregateToWeekly(daily: OHLCV[]): OHLCV[] {
  return rollUp(daily, weekStart);
}

/** Daily → monthly candles, calendar months starting on the 1st, 00:00 UTC. */
export function aggregateToMonthly(daily: OHLCV[]): OHLCV[] {
  return rollUp(daily, monthStart);
}

/**
 * Roll candles up into fixed `bucketMs` buckets aligned to the Unix epoch
 * (so 4h buckets start at 00:00, 04:00, … UTC). Input may be unsorted.
 */
export function aggregateCandles(candles: OHLCV[], bucketMs: number): OHLCV[] {
  return rollUp(candles, (timestamp) => Math.floor(timestamp / bucketMs) * bucketMs);
}

/** Start of the `timeframe` bucket containing `timestamp` (weeks on Monday, months on the 1st, UTC). */
export function bucketStart(timestamp: number, timeframe: Timeframe): number {
  if (timeframe === '1W') return weekStart(timestamp);
  if (timeframe === '1M') return monthStart(timestamp);
  const ms = timeframeToMs(timeframe);
  return Math.floor(timestamp / ms) * ms;
}

/** Roll finer candles up into `timeframe` candles using the bucket rules of `bucketStart`. */
export function aggregateToTimeframe(candles: OHLCV[], timeframe: Timeframe): OHLCV[] {
  return rollUp(candles, (timestamp) => bucketStart(timestamp, timeframe));
}
